import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getInvoice, uploadBilty, replaceBilty, sendWhatsAppV2, getWhatsAppTemplates, getSettings } from '../lib/api';
import { formatCurrency, formatDate, formatDateTime, getBiltyStatusColor } from '../lib/utils';
import { ArrowLeft, FileText, Download, Upload, RefreshCw, Send, User, Truck, Check, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [biltyApplicableFrom, setBiltyApplicableFrom] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('dispatch');
  const [customMessage, setCustomMessage] = useState('');
  const [showWhatsAppPanel, setShowWhatsAppPanel] = useState(false);
  const [sendBilty, setSendBilty] = useState(false);
  const [customPhone, setCustomPhone] = useState('');

  const loadInvoice = () => {
    Promise.all([
      getInvoice(id),
      getSettings(),
      getWhatsAppTemplates().catch(() => ({ data: { templates: [] } })),
    ]).then(([invRes, setRes, tmplRes]) => {
      setInvoice(invRes.data);
      setBiltyApplicableFrom(setRes.data?.bilty_applicable_from);
      setTemplates(tmplRes.data?.templates || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadInvoice(); }, [id]);

  const handleBiltyUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const isReplace = invoice.bilty_status === 'uploaded';
      if (isReplace) {
        await replaceBilty(id, fd);
      } else {
        await uploadBilty(id, fd);
      }
      toast.success('Bilty uploaded successfully!');
      loadInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleWhatsAppV2 = async (type, phone) => {
    if (!phone) { toast.error('No phone number available'); return; }
    setSending(true);
    try {
      const res = await sendWhatsAppV2({
        invoice_id: id,
        phone_number: phone.replace(/[^0-9]/g, ''),
        template: selectedTemplate,
        custom_message: selectedTemplate === 'custom' ? customMessage : '',
        recipient_type: type,
        send_bilty: sendBilty,
      });
      const biltyNote = res.data?.bilty_attached ? ' (with Bilty attached)' : '';
      toast.success(`WhatsApp notification sent to ${phone}${biltyNote}`);
      loadInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed (provider session may need activation)');
    }
    setSending(false);
  };

  const isBiltyEligible = () => {
    if (!biltyApplicableFrom) return true;
    if (!invoice?.bill_date) return false;
    return new Date(invoice.bill_date) >= new Date(biltyApplicableFrom);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-boss-violet border-t-transparent rounded-full" /></div>;
  if (!invoice) return <div className="text-center py-12 text-boss-text-muted">Invoice not found</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="invoice-detail-page">
      <Link to="/boss/fa/invoices" className="inline-flex items-center gap-2 text-sm text-boss-text-muted hover:text-boss-violet">
        <ArrowLeft size={16} /> Back to Invoices
      </Link>

      {/* Header Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-boss-text">Invoice {invoice.bill_no_full}</h1>
            <p className="text-sm text-boss-text-muted mt-1">
              {invoice.invoice_series} Series | FY {invoice.financial_year} | {formatDate(invoice.bill_date)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {invoice.is_cancelled && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">Cancelled</span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBiltyStatusColor(invoice.bilty_status)}`}>
              Bilty: {invoice.bilty_status || 'pending'}
            </span>
          </div>
        </div>

        {/* Amount & Key Info */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-boss-violet/5 p-4 rounded-xl border border-boss-violet/10">
            <div className="text-xs text-boss-text-muted">Grand Total</div>
            <div className="text-2xl font-bold text-boss-violet mt-1">{formatCurrency(invoice.amount_grand_total)}</div>
          </div>
          <div className="bg-boss-surface-2 p-4 rounded-xl">
            <div className="text-xs text-boss-text-muted">Bill No.</div>
            <div className="text-sm font-mono font-semibold mt-1">{invoice.bill_no_full}</div>
          </div>
          <div className="bg-boss-surface-2 p-4 rounded-xl">
            <div className="text-xs text-boss-text-muted">Bill Date</div>
            <div className="text-sm font-semibold mt-1">{formatDate(invoice.bill_date)}</div>
          </div>
          <div className="bg-boss-surface-2 p-4 rounded-xl">
            <div className="text-xs text-boss-text-muted">Invoice #</div>
            <div className="text-sm font-mono font-semibold mt-1">{invoice.invoice_number}</div>
          </div>
          <div className="bg-boss-surface-2 p-4 rounded-xl">
            <div className="text-xs text-boss-text-muted">WhatsApp</div>
            <div className="text-sm font-semibold mt-1">{invoice.whatsapp_sent ? `Sent ${formatDate(invoice.whatsapp_sent_at)}` : 'Not sent'}</div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Party Details */}
          <div>
            <h3 className="text-sm font-semibold text-boss-text flex items-center gap-2 mb-3">
              <User size={14} className="text-boss-violet" /> Party Details
            </h3>
            <div className="bg-boss-surface-2 rounded-xl p-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Name</span>
                <span className="font-medium text-right max-w-[200px] truncate">{invoice.party_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Mobile</span>
                <span className="font-mono">{invoice.party_mobile || '---'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">GSTIN</span>
                <span className="font-mono text-xs">{invoice.party_gstin || '---'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">PAN</span>
                <span className="font-mono text-xs">{invoice.pan_number || '---'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">State</span>
                <span>{invoice.party_state || '---'}</span>
              </div>
              {invoice.party_mobile && (
                <button
                  onClick={() => handleWhatsAppV2('party', invoice.party_mobile)}
                  disabled={sending}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-boss-success text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
                  data-testid="whatsapp-party-btn"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send WhatsApp to Party
                </button>
              )}
            </div>
          </div>

          {/* Dispatch Details */}
          <div>
            <h3 className="text-sm font-semibold text-boss-text flex items-center gap-2 mb-3">
              <Truck size={14} className="text-boss-violet" /> Dispatch Details
            </h3>
            <div className="bg-boss-surface-2 rounded-xl p-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Salesman</span>
                <span className="font-medium">{invoice.salesman_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Salesman Mobile</span>
                <span className="font-mono">{invoice.salesman_mobile || '---'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Transport</span>
                <span className="font-medium">{invoice.transport}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-boss-text-muted">Station</span>
                <span>{invoice.station}</span>
              </div>
              {invoice.salesman_mobile && (
                <button
                  onClick={() => handleWhatsAppV2('salesman', invoice.salesman_mobile)}
                  disabled={sending}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-boss-info text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
                  data-testid="whatsapp-salesman-btn"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send WhatsApp to Salesman
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Template Panel */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-boss-text flex items-center gap-2">
            <MessageSquare size={14} className="text-boss-violet" /> WhatsApp Notification Settings
          </h3>
          <button
            onClick={() => setShowWhatsAppPanel(!showWhatsAppPanel)}
            className="text-xs text-boss-violet hover:underline"
            data-testid="toggle-whatsapp-panel"
          >
            {showWhatsAppPanel ? 'Hide' : 'Show'} Options
          </button>
        </div>

        {showWhatsAppPanel && (
          <div className="space-y-4" data-testid="whatsapp-template-panel">
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1.5">Message Template</label>
              <select
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
                data-testid="whatsapp-template-select"
              >
                {templates.map(t => (
                  <option key={t.key} value={t.key}>{t.key.charAt(0).toUpperCase() + t.key.slice(1).replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            {selectedTemplate === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-boss-text-muted mb-1.5">Custom Message</label>
                <textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Type your custom message..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none resize-none"
                  data-testid="whatsapp-custom-message"
                />
              </div>
            )}

            {selectedTemplate !== 'custom' && templates.length > 0 && (
              <div className="bg-boss-surface-2 rounded-lg p-3">
                <div className="text-[10px] font-medium text-boss-text-muted mb-1">Template Preview</div>
                <pre className="text-xs text-boss-text whitespace-pre-wrap font-sans">
                  {templates.find(t => t.key === selectedTemplate)?.template || ''}
                </pre>
              </div>
            )}

            {/* Attach Bilty Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send-bilty"
                checked={sendBilty}
                onChange={e => setSendBilty(e.target.checked)}
                className="w-4 h-4 rounded border-boss-border text-boss-violet focus:ring-boss-violet/30"
                disabled={!invoice.bilty_doc_url}
                data-testid="send-bilty-checkbox"
              />
              <label htmlFor="send-bilty" className={`text-sm ${invoice.bilty_doc_url ? 'text-boss-text' : 'text-boss-text-muted'}`}>
                Attach Bilty document with WhatsApp message
              </label>
              {!invoice.bilty_doc_url && (
                <span className="text-[10px] text-boss-text-muted">(No bilty uploaded)</span>
              )}
            </div>

            {/* Send to Custom Number */}
            <div className="border-t border-boss-border pt-4">
              <label className="block text-xs font-medium text-boss-text-muted mb-1.5">Send to Custom Number</label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={customPhone}
                  onChange={e => setCustomPhone(e.target.value)}
                  placeholder="Enter phone number (e.g. 919876543210)"
                  className="flex-1 px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
                  data-testid="custom-phone-input"
                />
                <button
                  onClick={() => handleWhatsAppV2('custom', customPhone)}
                  disabled={sending || !customPhone.replace(/[^0-9]/g, '')}
                  className="flex items-center gap-2 px-4 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-[#5B4CF2] disabled:opacity-50 transition-colors whitespace-nowrap"
                  data-testid="whatsapp-custom-btn"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bilty Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <h3 className="text-sm font-semibold text-boss-text mb-4 flex items-center gap-2">
          <FileText size={14} className="text-boss-violet" /> Bilty Document
        </h3>

        {invoice.bilty_doc_url ? (
          <div className="flex items-center justify-between p-4 bg-boss-success-soft rounded-xl border border-boss-success/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-boss-success/10 flex items-center justify-center">
                <FileText size={20} className="text-boss-success" />
              </div>
              <div>
                <div className="text-sm font-medium text-boss-text">Bilty document uploaded</div>
                <div className="text-xs text-boss-text-muted">Status: {invoice.bilty_status}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={invoice.bilty_doc_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-4 py-2 bg-boss-success text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                data-testid="bilty-download-btn"
              >
                <Download size={14} /> Download
              </a>
              {isBiltyEligible() && (
                <label className="cursor-pointer">
                  <input
                    type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" className="hidden"
                    onChange={e => { if (e.target.files[0]) handleBiltyUpload(e.target.files[0]); }}
                    disabled={uploading}
                  />
                  <span className="flex items-center gap-1 px-4 py-2 bg-boss-warning text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Replace
                  </span>
                </label>
              )}
            </div>
          </div>
        ) : isBiltyEligible() ? (
          <div className="border-2 border-dashed border-boss-border rounded-xl p-8 text-center">
            <Upload size={32} className="mx-auto text-boss-text-muted mb-3" />
            <p className="text-sm text-boss-text-muted mb-4">No bilty uploaded yet. Upload a PDF or image file.</p>
            <label className="cursor-pointer">
              <input
                type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" className="hidden"
                onChange={e => { if (e.target.files[0]) handleBiltyUpload(e.target.files[0]); }}
                disabled={uploading}
                data-testid="bilty-upload-input"
              />
              <span className="inline-flex items-center gap-2 px-6 py-2.5 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-[#5B4CF2] transition-colors">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Uploading...' : 'Upload Bilty'}
              </span>
            </label>
          </div>
        ) : (
          <div className="p-4 bg-boss-warning-soft rounded-xl border border-boss-warning/20 text-sm text-boss-text flex items-center gap-2">
            <AlertCircle size={16} className="text-boss-warning flex-shrink-0" />
            Bilty upload is not available for this invoice (invoice date is before the configured bilty applicable date)
          </div>
        )}
      </div>
    </div>
  );
}
